import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import moment from 'moment';
import { getTestTitle, getScoreInterpretation } from '../../utils/testScoreUtils';

interface TestResult {
  _id: string;
  testType: string;
  score: number;
  createdAt: string;
}

const TestResultHistoryPage: React.FC = () => {
  const { testType } = useParams<{ testType: string }>();
  const navigate = useNavigate();
  const [results, setResults] = useState<TestResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await axios.get(`/api/test-results/${testType}`);
        setResults(response.data);
      } catch (err) {
        setError('결과를 불러오는 데 실패했습니다.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    if (testType) {
      fetchResults();
    }
  }, [testType]);

  if (isLoading) {
    return <div className="loading-container">로딩 중...</div>;
  }

  if (error) {
    return <div className="error-container">{error}</div>;
  }

  const title = getTestTitle(testType || null);

  return (
    <div className="history-container app-container">
      <header className="history-header">
        <button onClick={() => navigate(-1)} className="back-btn">
          ←
        </button>
        <h1>{title} 결과 이력</h1>
      </header>

      {results.length > 0 ? (
        <ul className="results-list">
          {results.map((result, index) => {
            const previousResult = results[index + 1];
            const difference = previousResult ? result.score - previousResult.score : null;

            const renderChange = (diff: number | null) => {
              if (diff === null) return null;
              if (diff > 0) return <span className="score-change increase">▲ (+{diff})</span>;
              if (diff < 0) return <span className="score-change decrease">▼ ({diff})</span>;
              return <span className="score-change same">-</span>;
            };

            const interpretation = getScoreInterpretation(testType || null, result.score);

            return (
              <li key={result._id} className="result-card">
                <div className="result-date">
                  {moment(result.createdAt).format('YYYY년 MM월 DD일')}
                </div>
                <div className="result-score">
                  <span>
                    총점: <strong>{result.score}</strong>
                  </span>
                  {renderChange(difference)}
                </div>
                {interpretation && (
                  <div className="result-interpretation">
                    <p>{interpretation}</p>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="no-results">
          <p>아직 저장된 검사 결과가 없습니다.</p>
          <button onClick={() => navigate(`/tests/${testType}`)} className="primary-btn">
            첫 검사 시작하기
          </button>
        </div>
      )}
    </div>
  );
};

export default TestResultHistoryPage;